import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { userService } from "../../apis/users";
import { User, UserResponse, VtcStatus } from "../../types/users";
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";
import { useNavigate } from "react-router-dom";
import { formatTextToCapitalise, formatTime, getDriverTypes } from "../../utils/util";
import { jwtDecode } from "jwt-decode";
import { getReferredUsersById } from "../../apis/referralPlans";
import { getCountriesData } from "../../apis/master";
import { partnerService, Partner } from "../../apis/partners";
import Link from "@mui/material/Link";

const DriverList: React.FC = () => {
  const [drivers, setDrivers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
  // Filter states
  const [status, setStatus] = useState<string | "">("all");
  const [driverType, setDriverType] = useState<string | "">("all");
  const [carType, setCarType] = useState<string | "">("all");
  const [minRejectedRides, setMinRejectedRides] = useState<number | "">("");
  const [maxRejectedRides, setMaxRejectedRides] = useState<number | "">("");
  const [minRides, setMinRides] = useState<number | "">("");
  const [maxRides, setMaxRides] = useState<number | "">("");
  const [country, setCountry] = useState<string | "">("all");
  const [city, setCity] = useState<string | "">("all");
  const [driverPlan, setDriverPlan] = useState<string | "">("all");
  const [partner, setPartner] = useState<string | "">("all");
  const [filtersVisible, setFiltersVisible] = useState(false); // State to toggle filters visibility
  const navigate = useNavigate();
  const [adminCountry, setAdminCountry] = useState<string>("all");
  // State for popup
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referredUsers, setReferredUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // New state for country data
  const [countryData, setCountryData] = useState<any[]>([]);
  const [availableRegions, setAvailableRegions] = useState<any[]>([]);
  
  // State for partners list
  const [partners, setPartners] = useState<Partner[]>([]);
  
  // State for rejected rides popup
  const [rejectedRidesPopupOpen, setRejectedRidesPopupOpen] = useState(false);
  const [selectedDriverRejectedRides, setSelectedDriverRejectedRides] = useState<Array<{ rideId: string; timestamp: Date | string }>>([]);
  const [selectedDriverForRejectedRides, setSelectedDriverForRejectedRides] = useState<User | null>(null);

  const loadDrivers = async (currentPage: number, limit: number, adminCountryLocal: string) => {
    try {
      let tempCountry = country;
      if (adminCountryLocal != "all") {
        tempCountry = adminCountryLocal;
      }
      const data: UserResponse = await userService.getUsers({
        userType: "driver",
        search,
        page: currentPage + 1, // API expects 1-based page index
        limit,
        status: status === "all" ? "" : status,
        driverType: driverType === "all" ? "" : driverType,
        carType: carType === "all" ? "" : carType,
        minRejectedRides,
        maxRejectedRides,
        minRides,
        maxRides,
        country:
          tempCountry === "all"
            ? ""
            : tempCountry === "uk"
            ? "GB"
            : tempCountry === "france"
            ? "FR"
            : tempCountry === "ivory-coast"
            ? "CI"
            : "",
        city: city === "all" ? "" : city,
        driverPlan: driverPlan === "all" ? "" : driverPlan,
        partnerId: partner === "all" ? "" : partner,
      });
      setDrivers(data.users);
      setTotal(data.total);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  // Function to fetch country data
  const fetchCountryData = async () => {
    try {
      const data = await getCountriesData();
      setCountryData(data);
    } catch (error) {
      console.error("Error fetching country data:", error);
    }
  };

  // Function to fetch partners list
  const fetchPartners = async () => {
    try {
      const response = await partnerService.getPartners(1, 1000); // Fetch all partners
      const partnersList = response.partners || response.data?.partners || [];
      setPartners(partnersList);
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  // Function to update available regions when country changes
  const updateAvailableRegions = (selectedCountry: string) => {
    if (selectedCountry === "all") {
      setAvailableRegions([]);
      return;
    }

    const countryInfo = countryData.find((c: any) => c.country_code === selectedCountry);
    if (countryInfo && countryInfo.regions) {
      setAvailableRegions(countryInfo.regions);
    } else {
      setAvailableRegions([]);
    }
  };

  const fetchReferredUsers = async (userId: string) => {
    setLoading(true);
    try {
      const response: any = await getReferredUsersById(userId);
      if (!response) {
        setLoading(false);
        return;
      }
      // Check if the response is valid

      if (response.status) {
        setReferredUsers(response.data.referred_users);
      } else {
        setReferredUsers([]);
      }
    } catch (error) {
      console.error("Error fetching referred users:", error);
      setReferredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReferralClick = (userId: string) => {
    setSelectedUserId(userId);
    setPopupOpen(true);
    fetchReferredUsers(userId);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setReferredUsers([]);
    setSelectedUserId(null);
  };

  const handleRejectedRidesClick = (driver: User) => {
    // Get rejected rides from driver - handle both old format (string[]) and new format (Array<{rideId, timestamp}>)
    const rejectedRides = (driver as any).rejectedRideIds || [];
    let formattedRides: Array<{ rideId: string; timestamp: Date | string }> = [];
    
    if (rejectedRides.length > 0) {
      // Check if it's the new format (array of objects)
      if (typeof rejectedRides[0] === 'object' && rejectedRides[0] !== null && 'rideId' in rejectedRides[0]) {
        formattedRides = rejectedRides as Array<{ rideId: string; timestamp: Date | string }>;
      } else {
        // Old format (array of strings) - convert to new format with current timestamp
        formattedRides = (rejectedRides as string[]).map(rideId => ({
          rideId,
          timestamp: new Date(), // Use current date as fallback
        }));
      }
    }
    
    setSelectedDriverRejectedRides(formattedRides);
    setSelectedDriverForRejectedRides(driver);
    setRejectedRidesPopupOpen(true);
  };

  const handleCloseRejectedRidesPopup = () => {
    setRejectedRidesPopupOpen(false);
    setSelectedDriverRejectedRides([]);
    setSelectedDriverForRejectedRides(null);
  };

  const handleRideIdClick = (rideId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/rides/${rideId}`);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded: any = jwtDecode(token);
      setAdminCountry(decoded.countryCode);
      loadDrivers(page, rowsPerPage, decoded.countryCode);
    }
  }, [
    page,
    rowsPerPage,
    search,
    status,
    driverType,
    carType,
    minRejectedRides,
    maxRejectedRides,
    minRides,
    maxRides,
    country,
    city,
    driverPlan,
    partner,
  ]);

  // Fetch country data and partners on component mount
  useEffect(() => {
    fetchCountryData();
    fetchPartners();
  }, []);

  // Update available regions when country changes
  useEffect(() => {
    updateAvailableRegions(country);
    // Reset city to "all" when country changes
    setCity("all");
  }, [country, countryData]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page on rows per page change
  };

  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<any>>) => (value: any) => {
      setter(value);
      setPage(0); // Reset to the first page when a filter changes
    };
  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    navigate(`/driver-documentation/${id}`);
  };
  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box style={{ width: "100%", padding: "20px", marginTop: "5%" }}>
          <Box>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}
            >
              <Typography style={{ fontWeight: 700, fontSize: 20 }}>Drivers</Typography>
            </div>
            {/* Toggle Filters Button */}
            <Button
              variant="outlined"
              onClick={() => setFiltersVisible((prev) => !prev)}
              sx={{ marginBottom: 2 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>
            {/* Filters section */}
            {filtersVisible && (
              <Box>
                <TextField
                  label="Search Drivers by name, email & phone number"
                  value={search}
                  onChange={handleSearchChange}
                  fullWidth
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    marginBottom: 2,
                    marginTop: 2,
                  }}
                >
                  {/* Status Filter */}
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      onChange={(e) => handleFilterChange(setStatus)(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="document_verified">Document Verified</MenuItem>
                      <MenuItem value="bank_details_added">Bank Details Added</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Driver Type Filter */}
                  <FormControl fullWidth>
                    <InputLabel>Driver Type</InputLabel>
                    <Select
                      value={driverType}
                      onChange={(e) => handleFilterChange(setDriverType)(e.target.value)}
                      label="Driver Type"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="professional">Shared Drivers</MenuItem>
                      <MenuItem value="delivery">Delivery</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Car Type Filter */}
                  <FormControl fullWidth>
                    <InputLabel>Car Type</InputLabel>
                    <Select
                      value={carType}
                      onChange={(e) => handleFilterChange(setCarType)(e.target.value)}
                      label="Vehicle Type"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="NOCAR">No Car</MenuItem>
                      <MenuItem value="CLASSIC">Classic</MenuItem>
                      <MenuItem value="GREEN">Green or Comfort for IC</MenuItem>
                      <MenuItem value="VAN">Van</MenuItem>
                      <MenuItem value="MOTOR_BIKE">Motor Bike</MenuItem>
                      <MenuItem value="TRIKE">Trike/Van...</MenuItem>
                      <MenuItem value="MEDIUM_TRUCK">{"Medium Truck (< 1.5 ton)"}</MenuItem>
                      <MenuItem value="LARGE_TRUCK">{"Large Truck (> 1.5 Ton)"}</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Min & Max Rejected Rides */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="Min Rejected Rides"
                      value={minRejectedRides}
                      onChange={(e) =>
                        handleFilterChange(setMinRejectedRides)(
                          e.target.value ? +e.target.value : ""
                        )
                      }
                      type="number"
                      fullWidth
                    />
                    <TextField
                      label="Max Rejected Rides"
                      value={maxRejectedRides}
                      onChange={(e) =>
                        handleFilterChange(setMaxRejectedRides)(
                          e.target.value ? +e.target.value : ""
                        )
                      }
                      type="number"
                      fullWidth
                    />
                  </Box>

                  {/* Min & Max Total Rides */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="Min Total Rides"
                      value={minRides}
                      onChange={(e) =>
                        handleFilterChange(setMinRides)(e.target.value ? +e.target.value : "")
                      }
                      type="number"
                      fullWidth
                    />
                    <TextField
                      label="Max Total Rides"
                      value={maxRides}
                      onChange={(e) =>
                        handleFilterChange(setMaxRides)(e.target.value ? +e.target.value : "")
                      }
                      type="number"
                      fullWidth
                    />
                  </Box>

                  {/* Country Filter */}
                  {adminCountry === "all" && (
                    <FormControl fullWidth>
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={country}
                        onChange={(e) => handleFilterChange(setCountry)(e.target.value)}
                        label="Country"
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="uk">UK</MenuItem>
                        <MenuItem value="france">France</MenuItem>
                        <MenuItem value="ivory-coast">Ivory Coast</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {/* City Filter - Updated to use dynamic regions */}
                  <FormControl fullWidth>
                    <InputLabel>City</InputLabel>
                    <Select value={city} onChange={(e) => setCity(e.target.value)} label="City">
                      <MenuItem value="all">All</MenuItem>
                      {availableRegions.map((region: any) => (
                        <MenuItem key={region.region_code} value={region.region_name}>
                          {region.region_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Driver Plan Filter */}
                  <FormControl fullWidth>
                    <InputLabel>Driver Plan</InputLabel>
                    <Select
                      value={driverPlan}
                      onChange={(e) => handleFilterChange(setDriverPlan)(e.target.value)}
                      label="Driver Plan"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="is_premium">Premium</MenuItem>
                      <MenuItem value="is_community">Community</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Partner Filter */}
                  <FormControl fullWidth>
                    <InputLabel>Partner</InputLabel>
                    <Select
                      value={partner}
                      onChange={(e) => handleFilterChange(setPartner)(e.target.value)}
                      label="Partner"
                    >
                      <MenuItem value="all">All</MenuItem>
                      {partners.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.firstName} {p.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              showLastButton
              showFirstButton
            />
            <TableContainer component={Paper}>
              <Table>
                <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Total Rides</TableCell>
                    <TableCell>Rejected Rides</TableCell>
                    <TableCell>Country</TableCell>
                    {/* <TableCell>City</TableCell> */}
                    <TableCell>Driver Type</TableCell>
                    <TableCell>Shared Ride Enabled</TableCell>
                    <TableCell>Is Teracar Driver?</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Driver Plan</TableCell>
                    <TableCell>Car Type</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Signed up at</TableCell>
                    <TableCell>Referral Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drivers.map((driver) => {
                    return (
                      <TableRow
                        key={driver._id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          cursor: "pointer",
                        }}
                        hover
                        onClick={(event) => {
                          if (
                            !(event.target as HTMLElement).closest('td[data-referral-cell="true"]')
                          ) {
                            handleClick(event, driver._id);
                          }
                        }}
                      >
                        <TableCell>{`${driver.firstName} ${driver.lastName}`}</TableCell>
                        <TableCell>{driver.email}</TableCell>
                        <TableCell>{driver.phone || "-"}</TableCell>
                        <TableCell>{driver.totalRideCount || 0}</TableCell>
                        <TableCell
                          onClick={(e) => {
                            e.stopPropagation();
                            if (driver.rejectedRideCount && driver.rejectedRideCount > 0) {
                              handleRejectedRidesClick(driver);
                            }
                          }}
                          style={{
                            cursor:
                              driver.rejectedRideCount && driver.rejectedRideCount > 0
                                ? "pointer"
                                : "default",
                            color:
                              driver.rejectedRideCount && driver.rejectedRideCount > 0
                                ? "blue"
                                : "black",
                          }}
                        >
                          {driver.rejectedRideCount || 0}
                        </TableCell>
                        <TableCell>{driver.address?.country || "-"}</TableCell>
                        {/* <TableCell>{driver.address?.city || "-"}</TableCell> */}
                        <TableCell>
                          {driver.driverType ? getDriverTypes(driver) : "Normal"}
                        </TableCell>
                        <TableCell>{driver.accept_shared_rides ? "Yes" : "No"}</TableCell>
                        <TableCell>{driver.is_teracar_driver ? "Yes" : "No"}</TableCell>
                        <TableCell>{formatTextToCapitalise(driver.vtcStatus)}</TableCell>
                        <TableCell>
                          {driver.is_premium_vtc
                            ? "Premiun"
                            : driver.is_community_vtc
                            ? "Community"
                            : "Normal"}
                        </TableCell>
                        <TableCell>
                          {driver.address?.country === "CI" && driver.carType === "GREEN"
                            ? "COMFORT"
                            : driver.carType
                            ? driver.carType
                            : "-"}
                        </TableCell>
                        <TableCell>{driver.rating}</TableCell>
                        <TableCell>{formatTime(driver.createdAt)}</TableCell>
                        <TableCell
                          data-referral-cell="true"
                          style={{
                            cursor:
                              driver.referral_count && driver.referral_count > 0
                                ? "pointer"
                                : "default",
                            color:
                              driver.referral_count && driver.referral_count > 0 ? "blue" : "black",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (driver.referral_count && driver.referral_count > 0) {
                              handleReferralClick(driver._id);
                            }
                          }}
                        >
                          {driver.referral_count || 0}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              showLastButton
              showFirstButton
            />
          </Box>
        </Box>
      </Box>
      {/* Popup for Referred Users */}
      <Dialog open={popupOpen} onClose={handleClosePopup} fullWidth maxWidth="sm">
        <DialogTitle>Referred Users</DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : referredUsers.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>User Type</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Total Rides</TableCell>
                  <TableCell>Rejected Rides</TableCell>
                  <TableCell>Referral Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referredUsers.map((user) => (
                  <TableRow key={user.referred_user_id}>
                    <TableCell>{user.referred_user_name}</TableCell>
                    <TableCell>{user.user_type}</TableCell>
                    <TableCell>{user.phone_number}</TableCell>
                    <TableCell>{user.total_ride_count}</TableCell>
                    <TableCell>{user.total_rejected_ride_count}</TableCell>
                    <TableCell>{formatTime(user.referral_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p>No referred users found.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Popup for Rejected Rides */}
      <Dialog open={rejectedRidesPopupOpen} onClose={handleCloseRejectedRidesPopup} fullWidth maxWidth="md">
        <DialogTitle>
          Rejected Rides - {selectedDriverForRejectedRides ? `${selectedDriverForRejectedRides.firstName} ${selectedDriverForRejectedRides.lastName}` : ""}
        </DialogTitle>
        <DialogContent>
          {selectedDriverRejectedRides.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ride ID</TableCell>
                  <TableCell>Rejected At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedDriverRejectedRides.map((rejectedRide, index) => (
                  <TableRow key={`${rejectedRide.rideId}-${index}`}>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => handleRideIdClick(rejectedRide.rideId, e)}
                        sx={{ cursor: "pointer" }}
                      >
                        {rejectedRide.rideId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {rejectedRide.timestamp
                        ? formatTime(
                            typeof rejectedRide.timestamp === "string"
                              ? rejectedRide.timestamp
                              : rejectedRide.timestamp.toISOString()
                          )
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography>No rejected rides found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectedRidesPopup}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverList;
